// src/lib/invoice-avoir-flow.js — L'AVOIR : ce que l'écran annonce, ce qui part.
//
// Chantier « L'avoir a un bouton ». CHEMIN COMPTABLE (CGI 289).
//
// Deux fonctions pures, calquées sur invoice-delete-flow.js :
//   - describeAvoir : les TROIS FAITS que l'écran doit dire AVANT le clic —
//       (1) quelle pièce est annulée (numéro, montant, mois, logement),
//       (2) quel numéro portera l'avoir (série AV-{prefix}, numéro PRÉVU
//           depuis les compteurs, jamais consommé ici),
//       (3) que l'originale RESTE : archivée, statut « annulée », inchangée.
//     Un avoir n'efface rien. S'il le laissait croire, on aurait remplacé un
//     mensonge par un autre.
//   - emitAvoirCoherent : orchestre le geste. Refuse côté écran ce que le
//     serveur refuserait de toute façon (brouillon, legacy sans ligne
//     `invoices`), bâtit le document AVEC ses jetons, envoie le numéro annoncé
//     comme `confirmOriginalNumber` (le serveur le compare à la base : liste
//     périmée ⇒ 409, rien d'écrit).
//
// Dépendances injectées (App.jsx fournit les implémentations réelles) :
//   - callEmitCreditNote({ invoiceId, html_snapshot, confirmOriginalNumber, meta })
//       → Promise<{ ok, message, credit_number?, original_number? }>
//   - buildHtml(entry) → { html } — genAvoirHTML, HTML porteur des jetons
//   - onError(message) / onSuccess(result) / done() — done() toujours appelé

import { isDraftInvoice } from "./invoices";
import { computePreviewNumber } from "./invoice-preview";

/** Série de l'avoir pour une facture de série `prefix` : 'AV-P', 'AV-V', 'AV-AVE'… */
export function creditNotePrefixFor(prefix) {
  if (!prefix || typeof prefix !== "string") return null;
  return `AV-${prefix}`;
}

/**
 * Les trois faits à annoncer. Lecture pure : rien n'est consommé.
 *
 * @param {object} args
 * @param {object} args.facture   - ligne Historique { num, invoice_number, prefix,
 *   montant, mois, log, prop, emailEnvoye, invoiceId, kind }
 * @param {Array}  [args.sequences] - retour de getInvoiceSequences (peut manquer)
 * @param {number} [args.year]      - année du geste (défaut : année courante)
 * @returns {{ possible: boolean, raison: string|null, originalNumber: string|null,
 *   creditPrefix: string|null, previewCreditNumber: string|null,
 *   montantAvoir: number, faits: string[] }}
 */
export function describeAvoir({
  facture,
  sequences = [],
  year = new Date().getFullYear(),
} = {}) {
  const f = facture || {};
  const montant = Number(f.montant) || 0;
  const base = {
    originalNumber: f.num || null,
    creditPrefix: creditNotePrefixFor(f.prefix),
    previewCreditNumber: null,
    montantAvoir: -Math.abs(montant),
    faits: [],
  };
  if (!f.invoiceId) {
    return {
      ...base,
      possible: false,
      raison:
        "Pièce sans ligne en base (entrée d'avril migrée) : aucun avoir possible depuis l'écran.",
    };
  }
  if (isDraftInvoice(f)) {
    return {
      ...base,
      possible: false,
      raison:
        "Un brouillon n'a consommé aucun numéro : supprime-le, un avoir n'a pas de sens ici.",
    };
  }
  if (f.kind === "credit_note") {
    return { ...base, possible: false, raison: "Un avoir ne s'annule pas." };
  }
  if (!base.creditPrefix) {
    return {
      ...base,
      possible: false,
      raison:
        "Série de la facture inconnue : impossible de dériver la série de l’avoir.",
    };
  }
  const previewCreditNumber = computePreviewNumber({
    prefix: base.creditPrefix,
    year,
    sequences,
  });
  const faits = [
    `Pièce annulée : facture N° ${f.num} — ${f.mois || "?"} — ${f.log || f.prop || "?"} — ${montant.toFixed(2).replace(".", ",")} €.`,
    previewCreditNumber
      ? `L'avoir portera le numéro ${previewCreditNumber} (prochain de la série ${base.creditPrefix} ; numéro prévu, confirmé à la création).`
      : `L'avoir sera le prochain numéro de la série ${base.creditPrefix} (compteur non lisible pour l'instant ; le numéro exact est attribué à la création).`,
    `La facture N° ${f.num} RESTE : archivée, statut « annulée », numéro / montant / document inchangés. Rien n'est effacé — l'avoir en est la contrepartie (${base.montantAvoir.toFixed(2).replace(".", ",")} €). Le mois redevient facturable.`,
  ];
  return { ...base, possible: true, raison: null, previewCreditNumber, faits };
}

/**
 * Émet l'avoir. Ordre : refus locaux → document → action serveur → retour.
 * Aucune écriture KV : la source fiscale est la table `invoices`, et l'avoir y
 * naît par la RPC — le factLog KV n'a jamais porté les avoirs.
 */
export async function emitAvoirCoherent(
  entry,
  { callEmitCreditNote, buildHtml, onError, onSuccess, done },
) {
  if (!entry) {
    done();
    return;
  }
  const desc = describeAvoir({ facture: entry });
  if (!desc.possible) {
    onError(desc.raison);
    done();
    return;
  }
  try {
    const { html } = buildHtml(entry);
    const res = await callEmitCreditNote({
      invoiceId: entry.invoiceId,
      html_snapshot: html,
      confirmOriginalNumber: entry.num,
      meta: {
        mois: entry.mois || null,
        prop_nom: entry.prop || null,
        log_nom: entry.log || null,
        email_dest: entry.emailDest || null,
      },
    });
    if (!res || !res.ok) {
      onError((res && res.message) || "L'avoir n'a pas été émis.");
      done();
      return;
    }
    onSuccess(res);
    done();
  } catch (e) {
    console.error("[emitAvoirCoherent] échec :", e);
    onError(
      "Erreur lors de l'émission de l'avoir : " +
        (e?.message || JSON.stringify(e)),
    );
    done();
  }
}
